import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { FileText, Home, FolderOpen, Settings, LogOut, User, Globe, BarChart3, Palette } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Header = () => {
  const { user, token, logout, isAdmin } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="header" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo — always links to public landing page "/". */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src="/evolutt_logo_dark.png" alt="Evolutt" className="h-8 w-auto" />
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {/* Início — always goes to the public landing page "/" for both
                anonymous and authenticated users. Authenticated users keep
                full nav access (Meus CVs, Backoffice, ...) via the other links. */}
            <Link
              to="/"
              className={`nav-link flex items-center gap-2 ${isActive('/') ? 'active' : ''}`}
              data-testid="nav-home"
            >
              <Home className="w-4 h-4" />
              {t('home')}
            </Link>
            
            {user && (
              <>
                <Link
                  to="/cv/new"
                  className={`nav-link flex items-center gap-2 ${isActive('/cv/new') ? 'active' : ''}`}
                  data-testid="nav-create-cv"
                >
                  <FileText className="w-4 h-4" />
                  {t('createCV')}
                </Link>
                <Link
                  to="/cvs"
                  className={`nav-link flex items-center gap-2 ${isActive('/cvs') ? 'active' : ''}`}
                  data-testid="nav-my-cvs"
                >
                  <FolderOpen className="w-4 h-4" />
                  {t('myCVs')}
                </Link>
              </>
            )}
            
            {isAdmin() && (
              <>
                <Link
                  to="/dashboard"
                  className={`nav-link flex items-center gap-2 ${isActive('/dashboard') ? 'active' : ''}`}
                  data-testid="nav-dashboard"
                >
                  <BarChart3 className="w-4 h-4" />
                  Dashboard
                </Link>
                <Link
                  to="/backoffice"
                  className={`nav-link flex items-center gap-2 ${isActive('/backoffice') ? 'active' : ''}`}
                  data-testid="nav-backoffice"
                >
                  <Settings className="w-4 h-4" />
                  {t('backoffice')}
                </Link>
              </>
            )}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <div className="flex items-center gap-1 bg-[#f0fdfa] rounded-md p-1" data-testid="language-selector">
              <button
                onClick={() => setLanguage('pt')}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                  language === 'pt' 
                    ? 'bg-[#1E3C3C] text-white' 
                    : 'text-[#64748b] hover:text-[#1E3C3C]'
                }`}
                data-testid="lang-pt-btn"
              >
                PT
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                  language === 'en' 
                    ? 'bg-[#1E3C3C] text-white' 
                    : 'text-[#64748b] hover:text-[#1E3C3C]'
                }`}
                data-testid="lang-en-btn"
              >
                EN
              </button>
            </div>

            {/* Auth */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-[#1E3C3C] flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="text-sm text-muted-foreground">
                    {user.email}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" />
                    {t('profile')}
                  </DropdownMenuItem>
                  {isAdmin() && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/backoffice')} data-testid="menu-backoffice">
                        <Settings className="w-4 h-4 mr-2" />
                        {t('backoffice')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/template')} data-testid="menu-admin-template">
                        <Palette className="w-4 h-4 mr-2" />
                        {language === 'pt' ? 'Template CV' : 'CV Template'}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                >
                  {t('login')}
                </Button>
                <Button
                  className="btn-primary"
                  onClick={() => navigate('/register')}
                  data-testid="register-btn"
                >
                  {t('register')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
